import React from "react";
import get from "lodash/get";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import * as constants from "@redbubble/design-system/react/constants";
import Box from "@redbubble/design-system/react/Box";
import ChevronRightIcon from "@redbubble/design-system/react/Icons/ChevronRight";
import Text from "@redbubble/design-system/react/Text";
import TextLink from "@redbubble/design-system/react/TextLink";
import Comments from "./Comments";
import CommentsLoadingState from "./CommentsLoadingState";
import NewComment from "./NewComment";
import styles from "./CommentsSection.scss";
import messages from "./messages";
import { commentsShape } from "../../../../../containers/apollo/useComments";
import ErrorMessage from "./ErrorMessage";

const CommentsSection = (props) => {
  const {
    artistId,
    comments,
    commentsError,
    commentsLoading,
    isArtist,
    isLoggedIn,
    locale,
    logEvent,
    openLoginSignupModal,
    userAccountLoading,
    userInfoLoading,
    workId,
  } = props;

  const commentsCount = comments.length;
  const currentUserHasAvatar = get(props, "userAccount.hasAvatar");
  const currentUserAvatar = currentUserHasAvatar
    ? get(props, "userInfo.avatar")
    : null;
  const currentUserDisplayName = get(props, "userAccount.displayName");
  const currentUserId = get(props, "userInfo.userId");
  const hasComments =
    !commentsLoading && Array.isArray(comments) && comments.length !== 0;

  const LoginSignupMessage = () => {
    const handleClick = (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      window.scrollTo(0, 0); // Recaptcha v2 hack-fix

      openLoginSignupModal({});
    };

    return (
      <Box style={{ textAlign: "center", marginBottom: "40px" }}>
        {hasComments && (
          <Box paddingRight="xs" display="inline-block">
            <Text type="body">
              <FormattedMessage {...messages.joinTheConversation} />
            </Text>
          </Box>
        )}
        <Box display="inline-block">
          <TextLink onClick={handleClick} intent={constants.PRIMARY}>
            <FormattedMessage {...messages.loginSignupLink} />
            <ChevronRightIcon size="medium" />
          </TextLink>
        </Box>
      </Box>
    );
  };

  const NoCommentsMessage = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        marginBottom="m"
        style={{ textAlign: "center" }}
      >
        <Text type="display5">
          <FormattedMessage {...messages.showArtistLove} />
        </Text>
        <Text>
          <FormattedMessage {...messages.showArtistAppreciation} />
        </Text>
      </Box>
    );
  };

  return (
    <Box className={styles.commentsSectionContainer}>
      {commentsLoading ? (
        <CommentsLoadingState />
      ) : (
        <>
          {isLoggedIn && (
            <NewComment
              avatar={currentUserAvatar}
              commentsCount={commentsCount}
              displayName={currentUserDisplayName}
              isArtist={isArtist}
              logEvent={logEvent}
              userId={currentUserId}
              userAccountLoading={userAccountLoading}
              userInfoLoading={userInfoLoading}
              workId={workId}
            />
          )}
          {!hasComments && !commentsError && <NoCommentsMessage />}
          {!isLoggedIn && !commentsError && <LoginSignupMessage />}
          {commentsError && <ErrorMessage message={messages.errorMessage} />}
          {hasComments && (
            <>
              <Comments
                avatar={currentUserAvatar}
                artistId={artistId}
                comments={comments}
                commentsCount={commentsCount}
                displayName={currentUserDisplayName}
                isArtist={isArtist}
                locale={locale}
                logEvent={logEvent}
                userId={currentUserId}
                workId={workId}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};

CommentsSection.propTypes = {
  artistId: PropTypes.string.isRequired,
  comments: commentsShape,
  commentsError: PropTypes.shape({ message: PropTypes.string }),
  commentsLoading: PropTypes.bool,
  isArtist: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  locale: PropTypes.string,
  logEvent: PropTypes.func,
  openLoginSignupModal: PropTypes.func,
  userAccountLoading: PropTypes.bool,
  userInfoLoading: PropTypes.bool,
  workId: PropTypes.string.isRequired,
};

CommentsSection.defaultProps = {
  comments: [],
  commentsError: null,
  commentsLoading: true,
  isArtist: false,
  isLoggedIn: false,
  locale: "en",
  logEvent: () => {},
  openLoginSignupModal: () => {},
  userAccountLoading: true,
  userInfoLoading: true,
};

export default CommentsSection;
